import {
  Flag,
  FlagOutlined,
  Key,
  KeyOutlined,
  Link,
  LinkOutlined,
  MonetizationOn,
  MonetizationOnOutlined,
  PieChart,
  PieChartOutline,
} from "@mui/icons-material";
import { Typography } from "@mui/material";
import { ReactElement } from "react";
import { RouteObject } from "react-router-dom";

import { UserTenantRole } from "~frontend/__generated__/graphql";
import FullPageCard from "~frontend/components/FullPageCard";
import Layout, {
  useBackToSubaccountListItem,
} from "~frontend/components/Layout";
import { InternalLink } from "~frontend/components/Link";
import LogOutLink from "~frontend/components/LogOutLink";
import Redirect from "~frontend/components/Redirect";
import Budgets from "~frontend/components/icons/Budgets";
import BudgetsFilled from "~frontend/components/icons/BudgetsFilled";
import Export from "~frontend/components/icons/Export";
import ExportFilled from "~frontend/components/icons/ExportFilled";
import OptOut from "~frontend/components/icons/OptOut";
import OptOutFilled from "~frontend/components/icons/OptOutFilled";
import Person from "~frontend/components/icons/Person";
import PersonFilled from "~frontend/components/icons/PersonFilled";
import Settings from "~frontend/components/icons/Settings";
import SettingsFilled from "~frontend/components/icons/SettingsFilled";
import SubaccountsIcon from "~frontend/components/icons/Subaccounts";
import SubaccountsFilled from "~frontend/components/icons/SubaccountsFilled";
import Usage from "~frontend/components/icons/Usage";
import UsageFilled from "~frontend/components/icons/UsageFilled";
import CacheClearingApolloProvider from "~frontend/providers/CacheClearingApolloProvider";
import {
  useCurrentUser,
  useCurrentUserIsStaff,
  useTenant,
} from "~frontend/providers/LoggedInUserProvider";
import { useFeatureFlag } from "~frontend/providers/LoggedInUserProvider/context";

import OrgAnalytics from "./analytics";
import { ApiKeys } from "./apiKeys";
import ApiDocumentation from "./apiKeys/ApiDocumentation";
import { OrganizationBudgets } from "./budgets";
import OrgExports from "./exports";
import DataDictionary from "./exports/DataDictionary";
import Flags from "./flags";
import { OptOuts } from "./optOuts";
import { Pricing } from "./pricing";
import { OrganizationSettings } from "./settings";
import Shortlinks from "./shortlinks";
import { Subaccounts } from "./subaccounts";
import OrganizationUsage from "./usage";
import OrganizationUsers from "./users";

export function OrganizationLayout(): ReactElement {
  const tenant = useTenant()!;
  const isStaff = useCurrentUserIsStaff();
  const user = useCurrentUser();
  const apiKeysEnabled = useFeatureFlag("apiKeys");
  const budgetsEnabled = useFeatureFlag("budgets");

  const secondaryItems = useBackToSubaccountListItem();

  if (!user?.tenantRole || user.tenantRole === UserTenantRole.None) {
    return (
      <FullPageCard title="Unauthorized">
        <Typography>
          Only Organization Owners are allowed to access this page.{" "}
          <InternalLink to="/">Go Home</InternalLink>
        </Typography>

        <LogOutLink userEmail={user?.email} />
      </FullPageCard>
    );
  }

  return (
    <CacheClearingApolloProvider>
      <Layout
        topBarTitle={tenant.name}
        topBarLink="/organization/settings"
        sidebarItems={[
          {
            label: "Settings",
            path: "/organization/settings",
            icon: <Settings />,
            activeIcon: <SettingsFilled />,
          },
          {
            label: "Subaccounts",
            path: "/organization/subaccounts",
            icon: <SubaccountsIcon />,
            activeIcon: <SubaccountsFilled />,
          },
          {
            label: "People",
            path: "/organization/users",
            icon: <Person />,
            activeIcon: <PersonFilled />,
          },
          {
            label: "Opt Outs",
            path: "/organization/opt-outs",
            icon: <OptOut />,
            activeIcon: <OptOutFilled />,
          },
          {
            label: "Pricing",
            path: "/organization/pricing",
            icon: <MonetizationOnOutlined />,
            activeIcon: <MonetizationOn />,
          },
          ...(tenant.subaccountsEnabled
            ? [
                {
                  label: "Usage",
                  path: "/organization/usage",
                  icon: <Usage />,
                  activeIcon: <UsageFilled />,
                },
              ]
            : []),
          ...(budgetsEnabled
            ? [
                {
                  label: "Budgets",
                  path: "/organization/budgets",
                  icon: <Budgets />,
                  activeIcon: <BudgetsFilled />,
                },
              ]
            : []),
          {
            label: "Data Exports",
            path: "/organization/exports",
            icon: <Export />,
            activeIcon: <ExportFilled />,
          },
          {
            label: "Shortlinks",
            path: "/organization/shortlinks",
            icon: <LinkOutlined />,
            activeIcon: <Link />,
          },
          ...(apiKeysEnabled
            ? [
                {
                  label: "API Keys",
                  path: "/organization/api-keys",
                  icon: <KeyOutlined />,
                  activeIcon: <Key />,
                },
              ]
            : []),
          {
            label: "Analytics",
            path: "/organization/analytics",
            icon: <PieChartOutline />,
            activeIcon: <PieChart />,
          },
          ...(isStaff
            ? [
                {
                  label: "Feature Flags",
                  path: "/organization/flags",
                  icon: <FlagOutlined />,
                  activeIcon: <Flag />,
                },
              ]
            : []),
        ]}
        sidebarSecondaryItems={secondaryItems}
      />
    </CacheClearingApolloProvider>
  );
}

export const organizationRoutes: RouteObject[] = [
  {
    index: true,
    element: <Redirect to="/organization/settings" />,
  },
  {
    path: "settings",
    element: <OrganizationSettings />,
  },
  {
    path: "subaccounts",
    element: <Subaccounts />,
  },
  {
    path: "users",
    element: <OrganizationUsers />,
  },
  {
    path: "opt-outs",
    element: <OptOuts />,
  },
  {
    path: "usage",
    element: <OrganizationUsage />,
  },
  {
    path: "exports",
    element: <OrgExports />,
  },
  {
    path: "exports/dictionary",
    element: <DataDictionary />,
  },
  {
    path: "pricing",
    element: <Pricing />,
  },
  {
    path: "shortlinks",
    element: <Shortlinks />,
  },
  {
    path: "flags",
    element: <Flags />,
  },
  {
    path: "api-keys",
    element: <ApiKeys />,
  },
  {
    path: "api-keys/documentation",
    element: <ApiDocumentation />,
  },
  {
    path: "analytics",
    element: <OrgAnalytics />,
  },
  {
    path: "budgets",
    element: <OrganizationBudgets />,
  },
];
